/**
 * Error message utilities for user-facing error displays.
 * Provides context-aware error messages with actionable guidance.
 */

interface ErrorContext {
  hasAppId: boolean;
  hasConfigId: boolean;
  hasState: boolean;
  hasRedirectUri: boolean;
}

export interface ErrorDetails {
  message: string;
  helpText: string;
  helpLink?: string;
}

/**
 * Converts technical errors into user-friendly messages with guidance.
 * Detects configuration issues and provides appropriate next steps.
 */
export function getUserFacingErrorMessage(
  error: string,
  context: ErrorContext,
): ErrorDetails {
  const defaultHelp = {
    helpText:
      "To connect WhatsApp Business to Zapier, please start the connection from within Zapier.",
  };

  // Missing configuration errors - most common case for direct navigation
  if (
    error.includes("App ID not configured") ||
    error.includes("must be accessed through Zapier") ||
    !context.hasAppId ||
    !context.hasConfigId
  ) {
    return {
      message: "This page must be accessed through Zapier",
      ...defaultHelp,
    };
  }

  // SDK loading errors - network/blocker issues
  if (
    error.includes("SDK") ||
    error.includes("loading") ||
    error.includes("timed out")
  ) {
    return {
      message: error,
      helpText:
        "Please check your internet connection and ad blocker settings, then try again.",
    };
  }

  // Generic error fallback
  return {
    message: error,
    ...defaultHelp,
  };
}
